// URL rules (mapping_type "url_rules"): how Windmill builds listing_url / rentio_url for a company.
// The model and the pattern semantics mirror Windmill's lib/url_rules.py and Compass' models/mapping.py.
// The functions here only drive the editor (validation, example URLs, the simple Rentio form);
// Windmill is what actually builds the URLs.

export type UrlConditionOp = "contains" | "not_contains" | "equals" | "not_equals" | "empty" | "not_empty";

export interface UrlCondition {
  field: string;
  op: UrlConditionOp;
  value?: string | null;
}

export interface UrlRule {
  when?: UrlCondition[];
  pattern?: string | null;
  function?: string | null;
  translations?: Record<string, string>;
}

export interface UrlRuleSet {
  rules: UrlRule[];
}

export type UrlType = "listing_url" | "rentio_url";
export type UrlRulesMappings = Partial<Record<UrlType, UrlRuleSet>>;

export const CONDITION_OPS: { value: UrlConditionOp; label: string; needsValue: boolean }[] = [
  { value: "contains", label: "contains", needsValue: true },
  { value: "not_contains", label: "does not contain", needsValue: true },
  { value: "equals", label: "equals", needsValue: true },
  { value: "not_equals", label: "does not equal", needsValue: true },
  { value: "empty", label: "is empty", needsValue: false },
  { value: "not_empty", label: "is not empty", needsValue: false },
];

export const TRANSFORMS: { token: string; note: string }[] = [
  { token: "|slug", note: "Villa à Jávea → villa-a-javea" },
  { token: "|lower", note: "lowercase" },
  { token: "|last_word", note: "last word of the value" },
  { token: "|map", note: "replace using the translations" },
];

// ### Pattern engine (same semantics as Windmill)

export function getPath(context: Record<string, unknown>, path: string): unknown {
  let current: unknown = context;
  for (const key of path.split(".")) {
    if (current === null || typeof current !== "object" || Array.isArray(current) || !(key in current)) return null;
    current = (current as Record<string, unknown>)[key];
  }
  return current ?? null;
}

export function slugify(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function applyTransform(name: string, value: string, translations: Record<string, string>): string | null {
  switch (name) {
    case "slug":
      return slugify(value);
    case "lower":
      return value.toLowerCase();
    case "last_word": {
      const words = value.split(/\s+/).filter(Boolean);
      return words.length ? words[words.length - 1] : "";
    }
    case "map": {
      const wanted = value.toLowerCase();
      for (const [from, to] of Object.entries(translations)) {
        if (from.toLowerCase() === wanted) return to;
      }
      return null;
    }
    default:
      return null;
  }
}

function renderPlaceholder(
  expression: string,
  context: Record<string, unknown>,
  translations: Record<string, string>,
): string | null {
  const [path, ...transforms] = expression.split("|").map((part) => part.trim());
  const raw = getPath(context, path);
  if (raw === null) return null;
  let value: string | null = String(raw);
  for (const name of transforms) {
    if (!value) return null;
    value = applyTransform(name, value, translations);
  }
  return value || null;
}

// Returns null when any placeholder can't be resolved: never a partial URL.
export function renderPattern(
  pattern: string,
  context: Record<string, unknown>,
  translations: Record<string, string>,
): string | null {
  let failed = false;
  const url = pattern.replace(/\{([^{}]+)\}/g, (_match, expression: string) => {
    const rendered = renderPlaceholder(expression, context, translations);
    if (rendered === null) {
      failed = true;
      return "";
    }
    return rendered;
  });
  return failed ? null : url;
}

function conditionHolds(condition: UrlCondition, context: Record<string, unknown>): boolean {
  const raw = getPath(context, condition.field);
  const actual = raw === null ? "" : String(raw).toLowerCase();
  const expected = (condition.value ?? "").toLowerCase();
  switch (condition.op) {
    case "contains":
      return actual.includes(expected);
    case "not_contains":
      return !actual.includes(expected);
    case "equals":
      return actual === expected;
    case "not_equals":
      return actual !== expected;
    case "empty":
      return actual === "";
    case "not_empty":
      return actual !== "";
  }
}

// Index of the first rule whose conditions all hold, or -1.
export function matchingRuleIndex(ruleSet: UrlRuleSet | undefined, context: Record<string, unknown>): number {
  return (ruleSet?.rules ?? []).findIndex((rule) => (rule.when ?? []).every((c) => conditionHolds(c, context)));
}

// Function rules are named functions registered in Windmill; the editor can't evaluate them.
export function resolveUrl(ruleSet: UrlRuleSet | undefined, context: Record<string, unknown>): string | null {
  const index = matchingRuleIndex(ruleSet, context);
  if (index === -1) return null;
  const rule = ruleSet!.rules[index];
  if (rule.pattern == null) return null;
  return renderPattern(rule.pattern, context, rule.translations ?? {});
}

export function validateRuleSet(ruleSet: UrlRuleSet): string[] {
  const errors: string[] = [];
  ruleSet.rules.forEach((rule, index) => {
    const label = `Rule ${index + 1}`;
    const hasPattern = rule.pattern != null && rule.pattern !== "";
    const hasFunction = rule.function != null && rule.function !== "";
    if (hasPattern === hasFunction) errors.push(`${label}: fill in the URL to build (or a function name, not both).`);
    (rule.when ?? []).forEach((condition) => {
      const needsValue = CONDITION_OPS.find((o) => o.value === condition.op)?.needsValue;
      if (!condition.field) errors.push(`${label}: a condition has no field.`);
      else if (needsValue && !condition.value) errors.push(`${label}: the condition on ${condition.field} has no value.`);
    });
  });
  return errors;
}

// ### Rentio form: a friendly view on the rentio_url rules

export type RentioIntegration = "zabun" | "whise" | "omnicasa";

export interface RentioOffice {
  id: string;
  sub: string;
}

export interface RentioForm {
  enabled: boolean;
  mode: "single" | "office";
  single: string;
  offices: RentioOffice[];
}

export const RENTIO_ID_FIELD: Record<RentioIntegration, string> = {
  zabun: "property_id",
  whise: "id",
  omnicasa: "ID",
};

export const EMPTY_RENTIO_FORM: RentioForm = { enabled: false, mode: "single", single: "", offices: [] };

const RENTAL_ONLY: UrlCondition[] = [{ field: "_offer", op: "equals", value: "rent" }];

function rentioPattern(integration: RentioIntegration, host: string): string {
  return `https://${host}.mijnhuurprofiel.be/${integration}/{${RENTIO_ID_FIELD[integration]}}`;
}

export function rentioFormToRuleSet(form: RentioForm, integration: RentioIntegration): UrlRuleSet {
  if (!form.enabled) return { rules: [] };
  if (form.mode === "office") {
    const translations: Record<string, string> = {};
    for (const office of form.offices) {
      if (office.id.trim() && office.sub.trim()) translations[office.id.trim()] = office.sub.trim();
    }
    return {
      rules: [{ when: RENTAL_ONLY, pattern: rentioPattern(integration, "{office_autoid|map}"), translations }],
    };
  }
  return { rules: [{ when: RENTAL_ONLY, pattern: rentioPattern(integration, form.single.trim()) }] };
}

// Returns "custom" when the stored rules are not what the form generates (edit those as rules).
export function ruleSetToRentioForm(
  ruleSet: UrlRuleSet | undefined,
  integration: RentioIntegration,
): RentioForm | "custom" {
  if (!ruleSet || ruleSet.rules.length === 0) return { ...EMPTY_RENTIO_FORM };
  if (ruleSet.rules.length !== 1) return "custom";
  const [rule] = ruleSet.rules;
  const when = rule.when ?? [];
  const isRentalOnly =
    when.length === 1 && when[0].field === "_offer" && when[0].op === "equals" && when[0].value === "rent";
  if (!isRentalOnly || rule.function || !rule.pattern) return "custom";

  const idPart = `.mijnhuurprofiel.be/${integration}/{${RENTIO_ID_FIELD[integration]}}`;
  const prefix = "https://";
  if (!rule.pattern.startsWith(prefix) || !rule.pattern.endsWith(idPart)) return "custom";
  const host = rule.pattern.slice(prefix.length, rule.pattern.length - idPart.length);

  if (integration === "zabun" && host === "{office_autoid|map}") {
    return {
      enabled: true,
      mode: "office",
      single: "",
      offices: Object.entries(rule.translations ?? {}).map(([id, sub]) => ({ id, sub })),
    };
  }
  if (host === "" || /[{}|]/.test(host) || Object.keys(rule.translations ?? {}).length > 0) return "custom";
  return { enabled: true, mode: "single", single: host, offices: [] };
}
